/**
 * Hover/focus elevation — a small upward `translateY` plus a shadow that
 * FADES IN rather than a `box-shadow` that grows.
 *
 * WHY A SEPARATE SHADOW LAYER
 * ---------------------------
 * Animating `box-shadow` (or `filter: drop-shadow`) repaints every frame — it
 * is not a compositor property. Instead this renders a second, absolutely
 * positioned layer that already HAS the elevated shadow baked in via a static
 * class, and only animates that layer's `opacity` (0 -> 1). The visible
 * content itself only ever moves via `transform: translateY`. Both properties
 * are compositor-only, so this is free to run at 60fps even mid-scroll.
 *
 * Wraps its children rather than replacing them, same reasoning as
 * `PressScale` — the interactive element underneath keeps its own semantics.
 */
import { motion, useReducedMotion } from "framer-motion";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { SPRING_SOFT } from "./springs";

export interface LiftProps {
  children: ReactNode;
  className?: string;
  /** How far the content rises on hover, in px. Defaults to 2. */
  distance?: number;
  /** Rounding for the shadow layer — should match the content's own radius. */
  rounded?: string;
}

const contentVariants = (distance: number) => ({
  rest: { y: 0 },
  hover: { y: -distance },
});

const shadowVariants = {
  rest: { opacity: 0 },
  hover: { opacity: 1 },
};

export function Lift({ children, className, distance = 2, rounded = "rounded-md" }: LiftProps) {
  const reduceMotion = useReducedMotion();

  return (
    <motion.div
      className={cn("relative inline-flex", className)}
      initial="rest"
      animate="rest"
      whileHover={reduceMotion ? undefined : "hover"}
      whileFocus={reduceMotion ? undefined : "hover"}
    >
      {!reduceMotion && (
        <motion.span
          aria-hidden="true"
          className={cn(
            "pointer-events-none absolute inset-0 shadow-[var(--cp-shadow-lg)]",
            rounded,
          )}
          variants={shadowVariants}
          transition={{ duration: 0.16 }}
          style={{ willChange: "opacity" }}
        />
      )}
      <motion.div
        className="relative h-full w-full"
        variants={reduceMotion ? undefined : contentVariants(distance)}
        transition={SPRING_SOFT}
        style={{ willChange: "transform" }}
      >
        {children}
      </motion.div>
    </motion.div>
  );
}
